const { InMemoryCache } = require('@apollo/client')
const atob = require('atob')
const btoa = require('btoa')

const matchTypename = obj => {
  if (!obj.id) {
    throw new Error(`Object requires an id: ${JSON.stringify(obj)}`)
  }

  return `${atob(obj.id).split(':')[0]}:${obj.id}`
}

/**
 * Fetch incoming edges and add them to the edges of the existing array.
 * Good for lists with pagination.
 * See https://www.apollographql.com/docs/react/caching/cache-field-behavior/#handling-pagination
 */
const paginationMerge = (existing, incoming, { args, mergeObjects, readField }) => {
  const existingEdges = existing ? existing.edges.slice(0) : []
  let incomingEdges = incoming.edges ?? []
  // Obtain a Set of all existing task IDs.
  const existingIdSet = new Set(existingEdges.map(({ node }) => readField('id', node)))
  // Remove incoming tasks already present in the existing data.
  incomingEdges = incomingEdges.filter(({ node }) => !existingIdSet.has(readField('id', node)))
  // Find the index of the task just before the incoming page of tasks.
  const afterIndex = existingEdges.findIndex(({ node }) => args.after === readField('id', node))
  if (afterIndex >= 0) {
    // If we found afterIndex, insert incoming after that index.
    existingEdges.splice(afterIndex + 1, 0, ...incomingEdges)
  } else {
    // Otherwise insert incoming at the end of the existing data.
    existingEdges.push(...incomingEdges)
  }

  const merged = mergeObjects(existing, incoming)

  return {
    ...merged,
    edges: existingEdges,
  }
}

/**
 * When new edges are fetched overwrite the existing edges in cache.
 * Good for lists where only certain amount of items are shown.
 */
const paginationOverwriteMerge = (existing, incoming) => {
  let existingEdges = existing ? existing.edges.slice(0) : []
  let incomingEdges = incoming.edges ?? []

  const edges = incomingEdges.length > 0 ? incomingEdges : existingEdges

  return {
    ...existing,
    ...incoming,
    edges,
  }
}

module.exports = () => {
  return new InMemoryCache({
    possibleTypes: {
      Node: [
        'Group',
        'AllEvents',
        'Artist',
        'Tag',
        'EventType',
        'WantedListing',
        'TicketAlert',
        'OrganizerProduct',
        'OrganizerShop',
        'Location',
        'City',
        'OrganizerBrand',
        'ActiveEvent',
        'ExpiredEvent',
        'Draft',
        'Conversation',
        'ExternalPrimaryTicketShopAlert',
        'FaqItem',
        'Testimonial',
        'Partner',
        'EventPartner',
        'VoucherListing',
        'OrganizerTicketingOrder',
        'EventQuestionnaire',
      ],
      GroupItem: ['AllEvents', 'ActiveEvent', 'ExpiredEvent', 'Group', 'Artist'],
      Event: ['AllEvents', 'ActiveEvent', 'ExpiredEvent', 'CancelledEvent'],
      User: ['PublicUser', 'Viewer', 'PrivateUser'],
      Listing: ['PublicListing', 'CheckoutListing'],
      CheckoutGenericRow: ['CheckoutTicketRow', 'CheckoutPayoutReversalRow'],
      FanExperience: ['ArtistExperience', 'EventExperience', 'LocationExperience'],
      SearchResultItem: ['EventResult', 'CityResult', 'LocationResult', 'ArtistResult'],
      FeedItem: [
        'ThumbnailEventCollection',
        'ThumbnailWithUrl',
        'ThumbnailBlog',
        'GroupCollection',
        'RecurringEventCollection',
      ],
      CaseMessage: ['SupportMessage', 'UserMessage', 'AutomatedMessage'],
      Error: [
        'BuyerHasBuyerBlockError',
        'CartCurrencyMismatchError',
        'BlockedFromBuyingForEventError',
        'BuyerCanNotBuyTicketsError',
        'BuyerNeedsWantedListingToBuyError',
        'CartLockedError',
        'NoTicketsCouldBeReservedError',
        'NotAllTicketsCouldBeReservedError',
        'TicketsCanOnlyBeBoughtAsSetError',
        'GeneralError',
      ],
    },
    typePolicies: {
      PrivateUser: { keyFields: matchTypename },
      PublicUser: {
        keyFields: matchTypename,
        fields: {
          activeTicketAlerts: {
            merge: false,
          },
        },
      },
      Uri: { merge: true },
      ListingPrice: { merge: true },
      ImportedFiles: { merge: false },
      ImplementationJourneyVerification: { keyFields: ['type'] },
      DraftState: { merge: true },
      SellingPriceSuggestion: { merge: true },
      DraftProfitSharing: { merge: true },
      Checkout: { merge: true },
      CheckoutTicketRow: {
        fields: {
          ticketGroups: {
            merge: false,
          },
        },
      },
      CheckoutTicketGroup: {
        keyFields: ['price', ['amount'], 'listing', ['id']],
      },
      Country: {
        keyFields: ['code'],
      },
      UserPhone: {
        keyFields: ['number'],
      },
      PayoutMethod: {
        keyFields: ({ __typename, name }) => `${__typename}:${btoa(name)}`,
      },
      PayoutMethodField: {
        keyFields: ({ __typename, name, value }) => `${__typename}:${btoa(`${name}:${value}`)}`,
      },
      Query: {
        fields: {
          activeEvents: {
            keyArgs: ['date', 'period', 'filter', 'dateRange'],
            merge: paginationMerge,
          },
          allBlogPosts: {
            keyArgs: [],
            merge: paginationMerge,
          },
          nearby: {
            merge: false,
          },
          search: {
            keyArgs: ['query', 'type'],
            merge: paginationMerge,
          },
          searchEvents: {
            keyArgs: ['query'],
            merge: paginationMerge,
          },
          testimonials: {
            keyArgs: ['language', 'minimumRating', 'minimumLength', 'maximumLength'],
            merge: paginationOverwriteMerge,
          },
        },
      },
      // Nodes...
      Viewer: {
        keyFields: matchTypename,
        fields: {
          conversations: {
            keyArgs: ['filter'],
            merge: paginationMerge,
          },
          fanExperiences: {
            keyArgs: [],
            merge: paginationMerge,
          },
          followedArtists: {
            keyArgs: [],
            merge: paginationMerge,
          },
          favoriteEvents: {
            keyArgs: [],
            merge: paginationMerge,
          },
          myEvents: {
            keyArgs: ['getActive', 'first'],
            merge: paginationMerge,
          },
          myListings: {
            keyArgs: ['filter'],
            merge: paginationMerge,
          },
          notificationInboxMessages: {
            keyArgs: [],
            merge: paginationMerge,
          },
          payouts: {
            keyArgs: [],
            merge: paginationMerge,
          },
          activeTicketAlerts: {
            keyArgs: [],
            merge: false,
          },
        },
      },
      Artist: {
        fields: {
          eventExperiences: {
            keyArgs: ['excludingViewerExperience'],
            merge: paginationMerge,
          },
        },
      },
      Location: {
        fields: {
          eventExperiences: {
            keyArgs: ['excludingViewerExperience'],
            merge: paginationMerge,
          },
        },
      },
      Tag: {
        fields: {
          artists: {
            keyArgs: [],
            merge: paginationMerge,
          },
        },
      },
      Group: {
        fields: {
          items: {
            keyArgs: ['filter'],
            merge: paginationMerge,
          },
        },
      },
      EventType: {
        fields: {
          listings: {
            keyArgs: ['filter', 'first'],
            merge: paginationMerge,
          },
        },
      },
      OrganizerBrand: {
        fields: {
          events: {
            keyArgs: ['statuses', 'orderBy'],
            merge: paginationMerge,
          },
        },
      },
      DraftTicket: {
        fields: {
          errors: {
            merge: false,
          },
        },
      },
      Case: {
        fields: {
          messages: {
            merge: false,
          },
        },
      },
    },
  })
}
